/* ============================================================
   KAGE — Tokyo Streetwear · theme.js
   Vanilla JS, no dependencies. Progressive enhancement.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Utilities ---------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  function formatMoney(cents) {
    const format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimal] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${decimal}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimal}`);
  }

  function trapFocus(container) {
    const focusable = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container);
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  /* ---------- Overlay + drawer manager ---------- */
  const overlay = $('[data-overlay]');
  let openDrawer = null;
  let releaseFocus = () => {};
  let lastTrigger = null;

  function showOverlay() { if (!overlay) return; overlay.hidden = false; requestAnimationFrame(() => overlay.classList.add('is-visible')); }
  function hideOverlay() { if (!overlay) return; overlay.classList.remove('is-visible'); setTimeout(() => { overlay.hidden = true; }, 350); }

  window.openDrawerById = function (id, trigger) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    if (openDrawer && openDrawer !== drawer) closeDrawers();
    lastTrigger = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.removeAttribute('aria-hidden');
    document.body.classList.add('drawer-open');
    showOverlay();
    openDrawer = drawer;
    releaseFocus = trapFocus(drawer);
    const focusEl = $('[data-autofocus]', drawer) || $('button, a, input', drawer);
    if (focusEl) setTimeout(() => focusEl.focus(), 120);
  };

  function closeDrawers() {
    if (!openDrawer) return;
    openDrawer.classList.remove('is-open');
    openDrawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
    hideOverlay();
    releaseFocus();
    if (lastTrigger && lastTrigger.focus) lastTrigger.focus();
    openDrawer = null;
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); window.openDrawerById(opener.getAttribute('data-drawer-open'), opener); return; }
    if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); closeDrawers(); return; }
  });
  if (overlay) overlay.addEventListener('click', closeDrawers);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeDrawers(); closeModals(); } });

  /* ---------- Header: scroll state, mega menu ---------- */
  const headerWrapper = $('[data-header-wrapper]');
  if (headerWrapper) {
    const onScroll = () => headerWrapper.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  $$('[data-menu-trigger]').forEach((trigger) => {
    const target = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!target) return;
    const parent = trigger.closest('li');
    let closeTimer;
    const open = () => { clearTimeout(closeTimer); $$('.mega-menu.is-open, .dropdown-menu.is-open').forEach((m) => { if (m !== target) m.classList.remove('is-open'); }); target.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); };
    const close = () => { target.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); };
    trigger.addEventListener('click', (e) => { e.preventDefault(); target.classList.contains('is-open') ? close() : open(); });
    (parent || trigger).addEventListener('mouseenter', open);
    (parent || trigger).addEventListener('mouseleave', () => { closeTimer = setTimeout(close, 180); });
    target.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    target.addEventListener('mouseleave', () => { closeTimer = setTimeout(close, 180); });
    document.addEventListener('click', (e) => { if (!target.contains(e.target) && !trigger.contains(e.target)) close(); });
  });

  /* Mobile nav accordions */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-mobile-submenu-toggle]');
    if (!toggle) return;
    e.preventDefault();
    const item = toggle.closest('.mobile-nav__item');
    const isOpen = item.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });

  /* ---------- Cart API ---------- */
  async function refreshCart(openAfter) {
    try {
      const res = await fetch(`${window.theme.routes.cart}?sections=cart-drawer&t=${Date.now()}`);
      const data = await res.json();
      const html = data.sections ? data.sections['cart-drawer'] : null;
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('#CartDrawer', doc);
        const current = $('#CartDrawer');
        if (fresh && current) {
          const wasOpen = current.classList.contains('is-open');
          current.innerHTML = fresh.innerHTML;
          if (wasOpen) current.classList.add('is-open');
        }
      }
      const countRes = await fetch(`${window.theme.routes.cart}.js`);
      const cart = await countRes.json();
      $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.dataset.count = cart.item_count; });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      if (openAfter) window.openDrawerById('CartDrawer');
    } catch (err) { console.error('Cart refresh failed', err); }
  }

  async function addToCart(body, button) {
    if (button) button.classList.add('is-loading');
    try {
      const res = await fetch(window.theme.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Could not add to cart');
      await refreshCart(true);
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
    } catch (err) {
      announce(err.message);
    } finally {
      if (button) button.classList.remove('is-loading');
    }
  }

  function announce(message) {
    let live = $('#a11y-announcer');
    if (!live) {
      live = document.createElement('div');
      live.id = 'a11y-announcer';
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      document.body.appendChild(live);
    }
    live.textContent = message;
  }

  /* Product form submit (main product + quick add) */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const button = $('[data-add-to-cart]', form);
    const formData = new FormData(form);
    addToCart({
      items: [{ id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }],
      sections: 'cart-drawer'
    }, button);
  });

  /* Quick add buttons (single-variant fast path) */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = Number(btn.getAttribute('data-quick-add'));
    if (variantId) { addToCart({ items: [{ id: variantId, quantity: 1 }] }, btn); }
    else if (btn.getAttribute('data-product-url')) { window.location.href = btn.getAttribute('data-product-url'); }
  });

  /* Cart line quantity + remove (drawer and cart page) */
  document.addEventListener('click', async (e) => {
    const qtyBtn = e.target.closest('[data-qty-change]');
    const removeBtn = e.target.closest('[data-line-remove]');
    if (!qtyBtn && !removeBtn) return;
    const el = qtyBtn || removeBtn;
    const line = el.closest('[data-line-key]');
    if (!line) return;
    e.preventDefault();
    const key = line.getAttribute('data-line-key');
    let quantity = 0;
    if (qtyBtn) {
      const input = $('input[name="quantity"]', line);
      quantity = Math.max(0, Number(input.value) + Number(qtyBtn.getAttribute('data-qty-change')));
    }
    line.style.opacity = '.4';
    try {
      await fetch(window.theme.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (document.body.classList.contains('template-cart') && !line.closest('#CartDrawer')) {
        window.location.reload();
      } else {
        await refreshCart(false);
      }
    } catch (err) { line.style.opacity = '1'; }
  });

  /* Free shipping bar */
  document.addEventListener('cart:updated', (e) => {
    const cart = e.detail;
    $$('[data-free-shipping-bar]').forEach((bar) => {
      const threshold = window.theme.freeShippingThreshold;
      if (!threshold) return;
      const remaining = Math.max(0, threshold - cart.total_price);
      const pct = Math.min(100, (cart.total_price / threshold) * 100);
      const fill = $('.free-shipping-bar__fill', bar);
      const text = $('.free-shipping-bar__text', bar);
      if (fill) fill.style.width = pct + '%';
      bar.classList.toggle('is-unlocked', remaining === 0);
      if (text) text.innerHTML = remaining === 0
        ? '<strong>Free shipping unlocked.</strong>'
        : `You are <strong>${formatMoney(remaining)}</strong> away from free shipping`;
    });
  });

  /* ---------- Variant selection ---------- */
  $$('[data-variant-picker]').forEach((picker) => {
    const productJson = $('[data-product-json]', picker.closest('[data-product-container]') || document);
    if (!productJson) return;
    const product = JSON.parse(productJson.textContent);
    const container = picker.closest('[data-product-container]');

    function currentOptions() {
      return $$('fieldset', picker).map((set) => {
        const checked = $('input:checked', set);
        return checked ? checked.value : null;
      });
    }

    function updateVariant() {
      const options = currentOptions();
      const variant = product.variants.find((v) => v.options.every((opt, i) => opt === options[i]));
      const addBtn = $('[data-add-to-cart]', container);
      const idInput = $('input[name="id"]', container);
      const priceEl = $('[data-product-price]', container);
      const stickyPrice = $('[data-sticky-price]');
      const status = $('[data-inventory-status]', container);

      if (!variant) {
        if (addBtn) { addBtn.setAttribute('disabled', ''); $('.btn__label', addBtn).textContent = 'Unavailable'; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceEl) {
        priceEl.innerHTML = variant.compare_at_price > variant.price
          ? `<span class="price__sale">${formatMoney(variant.price)}</span> <s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`
          : formatMoney(variant.price);
      }
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (addBtn) {
        const label = $('.btn__label', addBtn);
        if (variant.available) { addBtn.removeAttribute('disabled'); if (label) label.textContent = addBtn.dataset.labelAdd || 'Add to cart'; }
        else { addBtn.setAttribute('disabled', ''); if (label) label.textContent = 'Sold out'; }
      }
      if (status) {
        const low = Number(status.dataset.lowThreshold || 5);
        const qty = variant.inventory_quantity;
        const managed = variant.inventory_management;
        status.classList.remove('inventory-status--in', 'inventory-status--low', 'inventory-status--out');
        if (!variant.available) { status.classList.add('inventory-status--out'); status.textContent = 'Sold out'; }
        else if (managed && qty > 0 && qty <= low) { status.classList.add('inventory-status--low'); status.textContent = `Only ${qty} left — low stock`; }
        else { status.classList.add('inventory-status--in'); status.textContent = 'In stock, ready to ship'; }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant, product } }));
    }

    picker.addEventListener('change', updateVariant);
    updateVariant();
  });

  /* Quantity steppers (product form) */
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    const input = $('input', step.closest('.qty-input'));
    if (!input || input.name !== 'quantity' || step.closest('[data-line-key]')) return;
    e.preventDefault();
    input.value = Math.max(1, Number(input.value) + Number(step.getAttribute('data-qty-step')));
  });

  /* ---------- Sticky add to cart ---------- */
  const stickyAtc = $('[data-sticky-atc]');
  const productForm = $('form[data-product-form]');
  if (stickyAtc && productForm && 'IntersectionObserver' in window) {
    const buyBox = $('[data-buy-buttons]') || productForm;
    const io = new IntersectionObserver(([entry]) => {
      stickyAtc.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0 });
    io.observe(buyBox);
    const stickyBtn = $('[data-sticky-add]', stickyAtc);
    if (stickyBtn) stickyBtn.addEventListener('click', () => {
      productForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    });
  }

  /* ---------- Image lightbox ---------- */
  document.addEventListener('click', (e) => {
    const item = e.target.closest('[data-zoom-src]');
    if (!item) return;
    e.preventDefault();
    const sources = $$('[data-zoom-src]').map((el) => el.getAttribute('data-zoom-src'));
    let index = sources.indexOf(item.getAttribute('data-zoom-src'));
    const box = document.createElement('div');
    box.className = 'lightbox';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-label', 'Image zoom');
    box.innerHTML = `
      <img src="${sources[index]}" alt="">
      <button class="lightbox__close" aria-label="Close">✕</button>
      <button class="lightbox__nav lightbox__nav--prev" aria-label="Previous image">←</button>
      <button class="lightbox__nav lightbox__nav--next" aria-label="Next image">→</button>`;
    document.body.appendChild(box);
    document.body.style.overflow = 'hidden';
    const img = $('img', box);
    const show = (i) => { index = (i + sources.length) % sources.length; img.src = sources[index]; };
    box.addEventListener('click', (ev) => {
      if (ev.target.closest('.lightbox__nav--prev')) show(index - 1);
      else if (ev.target.closest('.lightbox__nav--next')) show(index + 1);
      else if (ev.target.closest('.lightbox__close') || ev.target === box) { box.remove(); document.body.style.overflow = ''; }
    });
    const keyHandler = (ev) => {
      if (ev.key === 'Escape') { box.remove(); document.body.style.overflow = ''; document.removeEventListener('keydown', keyHandler); }
      if (ev.key === 'ArrowLeft') show(index - 1);
      if (ev.key === 'ArrowRight') show(index + 1);
    };
    document.addEventListener('keydown', keyHandler);
    $('.lightbox__close', box).focus();
  });

  /* ---------- Deferred media (video play) ---------- */
  document.addEventListener('click', (e) => {
    const media = e.target.closest('.deferred-media[data-media-html]');
    if (!media) return;
    media.innerHTML = media.getAttribute('data-media-html');
    const video = $('video', media);
    if (video) { video.play().catch(() => {}); }
    media.classList.add('is-playing');
    media.removeAttribute('data-media-html');
  });

  /* ---------- Modals ---------- */
  function closeModals() { $$('[data-modal]').forEach((m) => { m.hidden = true; }); document.body.style.overflow = ''; }
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (opener) {
      e.preventDefault();
      const modal = document.getElementById(opener.getAttribute('data-modal-open'));
      if (modal) { modal.hidden = false; document.body.style.overflow = 'hidden'; const c = $('.modal__close', modal); if (c) c.focus(); }
      return;
    }
    if (e.target.closest('[data-modal-close]') || e.target.classList.contains('modal__backdrop')) { closeModals(); }
  });

  /* ---------- Predictive search ---------- */
  const searchInput = $('[data-predictive-input]');
  const searchResults = $('[data-predictive-results]');
  if (searchInput && searchResults) {
    let timer;
    searchInput.addEventListener('input', () => {
      clearTimeout(timer);
      const q = searchInput.value.trim();
      if (q.length < 2) { searchResults.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        try {
          const url = `${window.theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
          const res = await fetch(url);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = $('#PredictiveResults', doc);
          searchResults.innerHTML = inner ? inner.innerHTML : '';
        } catch (err) { /* silent */ }
      }, 250);
    });
  }

  /* ---------- Countdown timers ---------- */
  $$('[data-countdown]').forEach((el) => {
    const target = new Date(el.getAttribute('data-countdown')).getTime();
    if (Number.isNaN(target)) return;
    const units = {
      days: $('[data-cd-days]', el), hours: $('[data-cd-hours]', el),
      minutes: $('[data-cd-minutes]', el), seconds: $('[data-cd-seconds]', el)
    };
    function tick() {
      const diff = target - Date.now();
      if (diff <= 0) {
        el.dispatchEvent(new CustomEvent('countdown:ended', { bubbles: true }));
        const wrap = el.closest('[data-countdown-wrapper]');
        if (wrap && wrap.dataset.hideOnEnd === 'true') wrap.hidden = true;
        clearInterval(interval);
        return;
      }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor(diff / 3600000) % 24;
      const m = Math.floor(diff / 60000) % 60;
      const s = Math.floor(diff / 1000) % 60;
      if (units.days) units.days.textContent = String(d).padStart(2, '0');
      if (units.hours) units.hours.textContent = String(h).padStart(2, '0');
      if (units.minutes) units.minutes.textContent = String(m).padStart(2, '0');
      if (units.seconds) units.seconds.textContent = String(s).padStart(2, '0');
    }
    tick();
    const interval = setInterval(tick, 1000);
  });

  /* ---------- Scroll reveal ---------- */
  if ('IntersectionObserver' in window) {
    const reveals = $$('.reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    reveals.forEach((el) => io.observe(el));
  } else {
    $$('.reveal').forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- Carousel nav ---------- */
  $$('[data-carousel-wrapper]').forEach((wrap) => {
    const track = $('[data-carousel]', wrap);
    if (!track) return;
    const prev = $('[data-carousel-prev]', wrap);
    const next = $('[data-carousel-next]', wrap);
    const step = () => track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width + 16 : 320;
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step() * 2, behavior: 'smooth' }));
  });

  /* ---------- Facet filtering (auto submit) ---------- */
  const facetForm = $('#FacetFiltersForm');
  if (facetForm) {
    facetForm.addEventListener('change', () => {
      const params = new URLSearchParams(new FormData(facetForm));
      window.location.href = `${window.location.pathname}?${params.toString()}`;
    });
  }
  const sortSelect = $('[data-sort-by]');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ---------- Recently viewed ---------- */
  const RV_KEY = 'kage:recently-viewed';
  const productContainer = $('[data-product-container][data-product-handle]');
  if (productContainer) {
    try {
      const handle = productContainer.getAttribute('data-product-handle');
      const list = JSON.parse(localStorage.getItem(RV_KEY) || '[]').filter((h) => h !== handle);
      list.unshift(handle);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (err) { /* storage unavailable */ }
  }
  const rvSection = $('[data-recently-viewed]');
  if (rvSection) {
    try {
      const currentHandle = productContainer ? productContainer.getAttribute('data-product-handle') : null;
      const handles = JSON.parse(localStorage.getItem(RV_KEY) || '[]')
        .filter((h) => h !== currentHandle)
        .slice(0, Number(rvSection.dataset.limit || 4));
      if (!handles.length) { rvSection.closest('.section').hidden = true; }
      else {
        const grid = $('[data-rv-grid]', rvSection);
        Promise.all(handles.map((h) =>
          fetch(`/products/${h}?section_id=product-card-standalone`).then((r) => (r.ok ? r.text() : ''))
        )).then((cards) => {
          grid.innerHTML = cards.map((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const card = $('.product-card', doc);
            return card ? card.outerHTML : '';
          }).join('');
        });
      }
    } catch (err) { rvSection.closest('.section').hidden = true; }
  }

  /* ---------- Klaviyo newsletter enhancement ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-klaviyo-list]');
    if (!form) return;
    const listId = form.getAttribute('data-klaviyo-list');
    if (!listId) return; // fall back to Shopify customer form
    e.preventDefault();
    const email = $('input[type="email"]', form).value;
    const btn = $('button[type="submit"]', form);
    if (btn) btn.classList.add('is-loading');
    try {
      const body = new URLSearchParams({ g: listId, email });
      await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body
      });
      form.innerHTML = '<p class="form-message form-message--success">ようこそ — you are on the list.</p>';
    } catch (err) {
      form.submit();
    } finally { if (btn) btn.classList.remove('is-loading'); }
  });

})();
